import { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import BadgePill from "@/components/BadgePill";
import { apiError } from "@/lib/api";
import { WEEKDAYS } from "@/lib/constants";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Star, Calendar, Twitter, Facebook, Link as LinkIcon, Award, Lock } from "lucide-react";
import { VerifiedBadge } from "@/components/VerifiedBadge";

export default function ProviderProfile() {
  const { id } = useParams();
  const { user } = useAuth();
  const [provider, setProvider] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [svc, setSvc] = useState("");
  const [notes, setNotes] = useState("");
  const [frequency, setFrequency] = useState("once");
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [patients, setPatients] = useState([]);
  const [patientId, setPatientId] = useState("");
  const [slots, setSlots] = useState([]);
  const [slotDate, setSlotDate] = useState("");
  const [slotTime, setSlotTime] = useState("");
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get("book") === "1" && provider) setOpen(true);
  }, [provider]); // eslint-disable-line react-hooks/exhaustive-deps
  const when = slotDate && slotTime ? `${slotDate}T${slotTime}` : "";

  useEffect(() => {
    api.get(`/providers/${id}`).then(({ data }) => setProvider(data)).catch(() => {});
    api.get(`/providers/${id}/reviews`).then(({ data }) => setReviews(data)).catch(() => {});
    api.get(`/providers/${id}/slots`).then(({ data }) => setSlots(data.slots || [])).catch(() => {});
    if (user && user.role === "caregiver") {
      api.get("/caregiver/patients")
        .then(({ data }) => setPatients(data.filter((p) => p.status === "verified")))
        .catch(() => {});
    }
  }, [id, user]);

  const timesForDate = slots.find((s) => s.date === slotDate)?.times || [];

  const shareUrl = (slug) => `${process.env.REACT_APP_BACKEND_URL}/api/share/review/${slug}`;
  const onX = (slug) =>
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent("Check out this Care-4U.net review!")}&url=${encodeURIComponent(shareUrl(slug))}`, "_blank");
  const onFb = (slug) =>
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl(slug))}`, "_blank");
  const onCopy = (slug) => {
    navigator.clipboard.writeText(shareUrl(slug));
    toast.success("Share link copied!");
  };

  const addToCart = async () => {
    if (!user) return toast.error("Please login first");
    if (!svc) return toast.error("Choose a service");
    if (!when) return toast.error("Choose an available time slot");
    setBusy(true);
    try {
      await api.post("/cart", { provider_id: id, service: svc, scheduled_at: when, notes });
      toast.success("Saved to your cart! Confirm 2+ bookings together and save 5% on each 🛒");
      setOpen(false);
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Could not save to cart"); }
    finally { setBusy(false); }
  };

  const bookAndPay = async () => {
    if (!user) return toast.error("Please login to book");
    if (!svc) return toast.error("Choose a service");
    if (!when) return toast.error("Choose an available time slot");
    const isCaregiver = user.role === "caregiver";
    if (isCaregiver && !patientId) return toast.error("Select a patient to book for");
    setBusy(true);
    try {
      const { data: booking } = await api.post("/bookings", {
        provider_id: id, service: svc, scheduled_at: when, notes,
        ...(isCaregiver ? { client_id: patientId } : {}),
        ...(frequency !== "once" ? { frequency } : {}),
      });
      if (isCaregiver) {
        toast.success("Booking created for your patient");
        setOpen(false);
        setBusy(false);
        return;
      }
      const { data: checkout } = await api.post("/payments/checkout", {
        booking_id: booking.id, origin_url: window.location.origin,
      });
      window.location.href = checkout.url;
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Booking failed");
      setBusy(false);
    }
  };

  if (!provider) return <p className="text-center py-20 font-heading">Loading…</p>;

  const avail = provider.availability || {};
  const hasAvailability = Object.values(avail).some((d) => d?.enabled);

  const backupFactor = provider.backup_status ? (1 - (provider.backup_discount_pct || 20) / 100) : 1;
  const listPrice = provider.services?.find((s) => s.name === svc)?.price || 0;
  const price = Math.round(listPrice * backupFactor * 100) / 100;
  const fee = (price * 0.05).toFixed(2);

  return (
    <div className="max-w-5xl mx-auto px-4 py-10">
      <div className="card-metal p-8">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-heading font-black text-3xl text-slate-900">{provider.name}</h1>
            {(provider.nickname || provider.connected_since) && (
              <p className="mt-1 inline-flex items-center gap-1.5 text-sm font-semibold text-[#9b111e]" data-testid="profile-nickname">
                😊 You're connected{provider.nickname ? <> — they go by <b>"{provider.nickname}"</b></> : ""}{provider.connected_since ? ` · since ${new Date(provider.connected_since).toLocaleDateString()}` : ""}
              </p>
            )}
            <div className="mt-2 flex items-center gap-3 flex-wrap">
              <BadgePill tier={provider.badge} testId="profile-badge" />
              {provider.is_veteran && (
                <span className="inline-flex items-center gap-1 text-xs font-bold text-green-700 bg-green-50 border border-green-200 rounded-full px-2.5 py-1" data-testid="profile-veteran-badge">
                  <Star className="w-3 h-3 fill-green-600 text-green-600" /> Verified Veteran
                </span>
              )}
              {provider.backup_status && (
                <span className="inline-flex items-center gap-1 text-xs font-bold text-[#9b111e] bg-amber-50 border border-amber-300 rounded-full px-2.5 py-1" data-testid="profile-backup-badge">
                  Back-Up Pro · {provider.backup_discount_pct || 20}% off
                </span>
              )}
              <span className="flex items-center gap-1 font-bold text-[#9b111e]">
                <Star className="w-4 h-4 fill-[#9b111e]" /> {provider.avg_rating || 0}/10
              </span>
              <span className="text-sm text-slate-500">{provider.review_count || 0} reviews · {provider.years_experience}+ yrs exp</span>
            </div>
            {provider.bio && <p className="mt-4 text-slate-600 max-w-2xl">{provider.bio}</p>}
            {((provider.certificates || []).length > 0 || (provider.certifications || []).some((c) => c.reviewed)) && (
              <div className="mt-3 flex flex-wrap gap-2" data-testid="provider-certificates">
                {(provider.certificates || []).map((c) => (
                  <VerifiedBadge key={c.id} name={c.name}
                    className="badge-tier bg-slate-100 !text-[#9b111e] border-slate-200" />
                ))}
                {(provider.certifications || []).filter((c) => c.reviewed).map((c) => (
                  <VerifiedBadge key={c.id} name={c.name} testId={`profile-cert-badge-${c.id}`}
                    className="badge-tier bg-slate-100 !text-[#9b111e] border-slate-200" />
                ))}
              </div>
            )}
          </div>

          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <button className="btn-red-3d flex items-center gap-2" data-testid="open-booking-btn">
                <Calendar className="w-5 h-5" /> Book Now
              </button>
            </DialogTrigger>
            <DialogContent className="bg-white">
              <DialogHeader><DialogTitle className="font-heading">Book {provider.name}</DialogTitle>
                <DialogDescription>Choose a service and time, then pay securely.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                {user?.role === "caregiver" && (
                  <div>
                    <Label>Book for patient</Label>
                    <Select value={patientId} onValueChange={setPatientId}>
                      <SelectTrigger className="mt-1 bg-white" data-testid="booking-patient"><SelectValue placeholder="Select a verified patient" /></SelectTrigger>
                      <SelectContent>
                        {patients.map((p) => (
                          <SelectItem key={p.patient_id} value={p.patient_id}>{p.patient_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div>
                  <Label>Service</Label>
                  <Select value={svc} onValueChange={setSvc}>
                    <SelectTrigger className="mt-1 bg-white" data-testid="booking-service"><SelectValue placeholder="Choose a service" /></SelectTrigger>
                    <SelectContent>
                      {provider.services?.map((s) => (
                        <SelectItem key={s.name} value={s.name}>{s.name} — ${s.price}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Date</Label>
                    <Select value={slotDate} onValueChange={(v) => { setSlotDate(v); setSlotTime(""); }}>
                      <SelectTrigger className="mt-1 bg-white" data-testid="booking-date">
                        <SelectValue placeholder={slots.length ? "Pick a day" : "No openings"} />
                      </SelectTrigger>
                      <SelectContent>
                        {slots.map((s) => (
                          <SelectItem key={s.date} value={s.date}>{s.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Time</Label>
                    <Select value={slotTime} onValueChange={setSlotTime} disabled={!slotDate}>
                      <SelectTrigger className="mt-1 bg-white" data-testid="booking-time">
                        <SelectValue placeholder={slotDate ? "Pick a time" : "Pick a day first"} />
                      </SelectTrigger>
                      <SelectContent>
                        {timesForDate.map((t) => (
                          <SelectItem key={t} value={t} data-testid={`slot-${slotDate}-${t}`}>{t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {slots.length === 0 && (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2" data-testid="no-slots-note">
                    {provider.name.split(" ")[0]} has no open time slots in the next 30 days. Please check back soon.
                  </p>
                )}
                {hasAvailability && slots.length > 0 && (
                  <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-lg p-3" data-testid="provider-availability">
                    <b className="text-slate-700">{provider.name.split(" ")[0]}'s available hours:</b>
                    <div className="mt-1 space-y-0.5">
                      {WEEKDAYS.filter((d) => avail[d.key]?.enabled).map((d) => (
                        <div key={d.key} className="flex justify-between">
                          <span>{d.label}</span><span>{avail[d.key].start}–{avail[d.key].end}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div>
                  <Label>Repeat</Label>
                  <Select value={frequency} onValueChange={setFrequency}>
                    <SelectTrigger className="mt-1 bg-white" data-testid="booking-frequency"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="once">One-time service</SelectItem>
                      <SelectItem value="weekly">Recurring — Weekly</SelectItem>
                      <SelectItem value="biweekly">Recurring — Bi-weekly</SelectItem>
                      <SelectItem value="monthly">Recurring — Monthly</SelectItem>
                    </SelectContent>
                  </Select>
                  {frequency !== "once" && (
                    <p className="text-xs text-blue-700 mt-1" data-testid="recurring-note">
                      🔁 Subscribe & Save: after each completed visit, the next one is booked automatically at the same time — and every future repeat visit is <b>5% off with NO platform fee</b>. Manage or cancel anytime from your dashboard.
                    </p>
                  )}
                </div>
                <div>
                  <Label>Notes (optional)</Label>
                  <Input className="mt-1 bg-white" data-testid="booking-notes"
                    value={notes} onChange={(e) => setNotes(e.target.value)} />
                </div>
                {svc && (
                  <div className="text-sm bg-slate-50 border border-slate-200 rounded-lg p-3">
                    <div className="flex justify-between"><span>Service price</span><span>${listPrice.toFixed(2)}</span></div>
                    {provider.backup_status && (
                      <div className="flex justify-between text-green-700" data-testid="backup-discount-row"><span>Back-Up Pro discount ({provider.backup_discount_pct || 20}%)</span><span>-${(listPrice - price).toFixed(2)}</span></div>
                    )}
                    <div className="flex justify-between text-[#9b111e]"><span>Platform fee (5%)</span><span>${fee}</span></div>
                    <div className="flex justify-between font-bold mt-1 pt-1 border-t"><span>You pay</span><span>${price.toFixed(2)}</span></div>
                  </div>
                )}
                <p className="text-xs text-slate-500 flex items-center justify-center gap-1" data-testid="secure-note-booking">
                  <Lock className="w-3 h-3 text-green-600" /> Secured with <b className="mx-1">256-bit SSL encryption</b> · Payments by Stripe
                </p>
                <button onClick={bookAndPay} disabled={busy} data-testid="confirm-booking-btn" className="btn-red-3d w-full">
                  {busy ? "Processing…" : user?.role === "caregiver" ? "Create Booking for Patient" : "Book & Pay with Stripe"}
                </button>
                {user?.role === "client" && (
                  <button onClick={addToCart} disabled={busy} data-testid="add-to-cart-btn" className="btn-silver-3d w-full mt-2">
                    <span className="text-engraved text-sm">🛒 Save to Cart — bundle 2+ bookings for 5% off each</span>
                  </button>
                )}
              </div>
            </DialogContent>
          </Dialog>
        </div>

        <h2 className="font-heading font-black text-xl mt-8 mb-3 text-slate-900">Services Offered</h2>
        <div className="grid sm:grid-cols-2 gap-3">
          {provider.services?.map((s) => {
            const sale = (provider.sales || []).find((sl) => sl.service === s.name && new Date(sl.ends_at) > new Date());
            const factor = (provider.backup_status ? backupFactor : 1) * (sale ? 1 - sale.pct / 100 : 1);
            return (
            <div key={s.name} className="flex justify-between gap-3 border border-slate-200 rounded-lg p-3 bg-white">
              <div>
                <p className="font-semibold">{s.name}
                  {sale && <span className="ml-2 text-[11px] font-black text-white bg-[#9b111e] rounded-full px-2 py-0.5" data-testid={`sale-badge-${s.name}`}>🔥 {sale.pct.toFixed(0)}% OFF · ends {new Date(sale.ends_at).toLocaleDateString()}</span>}
                </p>
                <p className="text-xs text-slate-500">{s.category}</p>
                {s.description && <p className="text-xs text-slate-500 mt-1">{s.description}</p>}
              </div>
              <span className="font-heading font-black text-[#9b111e] flex-shrink-0 text-right">
                {factor < 1 ? (
                  <>
                    <span className="line-through text-slate-400 text-sm mr-1">${s.price}</span>
                    ${Math.round(s.price * factor)}
                  </>
                ) : <>${s.price}</>}
              </span>
            </div>
            );
          })}
        </div>
      </div>

      <h2 className="font-heading font-black text-2xl mt-10 mb-4 text-slate-900">Reviews</h2>
      {(() => {
        const highlights = reviews
          .filter((r) => r.rating >= 8 && r.photo_count > 0)
          .sort((a, b) => b.rating - a.rating)
          .flatMap((r) => (r.photo_idxs || []).map((i) => ({ r, i })))
          .slice(0, 8);
        return highlights.length > 0 ? (
          <div className="card-metal p-5 mb-6" data-testid="real-results-gallery">
            <h3 className="font-heading font-black text-lg text-slate-900 flex items-center gap-2">
              <Star className="w-5 h-5 fill-[#9b111e] text-[#9b111e]" /> Real Results
            </h3>
            <p className="text-sm text-slate-500 mt-0.5 mb-3">Photos from top-rated visits, shared by real clients.</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {highlights.map(({ r, i }) => (
                <a key={`${r.id}-${i}`} href={`${API}/reviews/${r.id}/photo/${i}`} target="_blank" rel="noreferrer"
                  className="relative group rounded-xl overflow-hidden border border-slate-200" data-testid={`highlight-photo-${r.id}-${i}`}>
                  <img src={`${API}/reviews/${r.id}/photo/${i}`} alt={`${r.service} result`} loading="lazy"
                    className="w-full h-32 object-cover group-hover:scale-105 transition-transform duration-300" />
                  <span className="absolute top-1.5 right-1.5 text-[11px] font-black text-white bg-[#9b111e]/90 rounded-full px-2 py-0.5">
                    ★ {r.rating}/10
                  </span>
                  <span className="absolute bottom-0 inset-x-0 text-[11px] text-white bg-gradient-to-t from-black/70 to-transparent px-2 pt-4 pb-1.5 truncate">
                    {r.service} · {r.client_name}
                  </span>
                </a>
              ))}
            </div>
          </div>
        ) : null;
      })()}
      <div className="space-y-3">
        {reviews.length === 0 && <p className="text-slate-500">No reviews yet.</p>}
        {reviews.map((r) => (
          <div key={r.id} className="card-metal p-4" data-testid={`review-${r.id}`}>
            <div className="flex justify-between items-center">
              <span className="font-bold text-[#9b111e] flex items-center gap-1">
                <Star className="w-4 h-4 fill-[#9b111e]" /> {r.rating}/10
              </span>
              <div className="flex items-center gap-1">
                <button onClick={() => onX(r.share_slug)} data-testid={`share-x-${r.id}`}
                  title="Share on X" className="p-2 rounded-full text-slate-500 hover:text-[#9b111e] hover:bg-red-50 transition-colors">
                  <Twitter className="w-4 h-4" />
                </button>
                <button onClick={() => onFb(r.share_slug)} data-testid={`share-fb-${r.id}`}
                  title="Share on Facebook" className="p-2 rounded-full text-slate-500 hover:text-[#9b111e] hover:bg-red-50 transition-colors">
                  <Facebook className="w-4 h-4" />
                </button>
                <button onClick={() => onCopy(r.share_slug)} data-testid={`share-copy-${r.id}`}
                  title="Copy share link" className="p-2 rounded-full text-slate-500 hover:text-[#9b111e] hover:bg-red-50 transition-colors">
                  <LinkIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
            <p className="text-slate-700 mt-1">{r.comment}</p>
            {r.photo_count > 0 && (
              <div className="flex gap-2 mt-2 flex-wrap" data-testid={`review-photos-${r.id}`}>
                {(r.photo_idxs || []).map((i) => (
                  <a key={i} href={`${API}/reviews/${r.id}/photo/${i}`} target="_blank" rel="noreferrer">
                    <img src={`${API}/reviews/${r.id}/photo/${i}`} alt="Review" loading="lazy"
                      className="w-20 h-20 object-cover rounded-lg border border-slate-200 hover:opacity-90 transition-opacity" data-testid={`review-photo-${r.id}-${i}`} />
                  </a>
                ))}
              </div>
            )}
            <p className="text-xs text-slate-400 mt-1">— {r.client_name} · {r.service}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
